import { projectsProjectReferenceSnapshotSchema } from '@crest/module-projects/contracts/project-reference';
import { z } from 'zod';
import { assetsPlacementAssetReferenceSchema } from './assets-placement.contract.js';
export const assetsLocationProvenanceCapability = 'assets.location-provenance.v1';
export const assetsLocationReviewCapability = 'assets.location-review.v1';
export const assetsLocationProviderSchema = z.enum(['kahi']);
export const assetsLocationPresenceSchema = z.enum(['present', 'departed']);
export const assetsLocationReviewReasonSchema = z.enum([
    'no-project-match',
    'project-inactive',
    'provider-location-unmapped',
]);
export const assetsLocationReviewStatusSchema = z.enum(['open', 'linked', 'ignored', 'resolved']);
export const assetsSourceLocationSnapshotSchema = z.strictObject({
    sourceLocationId: z.string().trim().min(1).max(240),
    name: z.string().trim().min(1).max(300),
    type: z.string().trim().min(1).max(120).nullable(),
    classification: z.enum(['base', 'jobsite', 'unknown']),
});
export const assetsSourceAssetSnapshotSchema = z.strictObject({
    sourceAssetId: z.string().trim().min(1).max(240),
    name: z.string().trim().min(1).max(300),
    type: z.string().trim().min(1).max(120).nullable(),
    assetTag: z.string().trim().min(1).max(160).nullable(),
    serialNumber: z.string().trim().min(1).max(160).nullable(),
    linkedAssetId: z.string().uuid().nullable().default(null),
});
export const assetsRecordLocationObservationSchema = z.strictObject({
    kind: z.literal('record'),
    commandId: z.string().uuid(),
    provider: assetsLocationProviderSchema,
    sourceEventId: z.string().trim().min(1).max(240),
    eventType: z.string().trim().min(1).max(160),
    occurredAt: z.string().datetime({ offset: true }),
    presence: assetsLocationPresenceSchema,
    reason: assetsLocationReviewReasonSchema,
    payloadSha256: z.string().regex(/^[a-f0-9]{64}$/),
    location: assetsSourceLocationSnapshotSchema,
    asset: assetsSourceAssetSnapshotSchema,
});
export const assetsLocationReviewAssetSchema = z.strictObject({
    id: z.string().uuid(),
    sourceAssetId: z.string().trim().min(1).max(240),
    name: z.string().trim().min(1).max(300),
    type: z.string().trim().min(1).max(120).nullable(),
    assetTag: z.string().trim().min(1).max(160).nullable(),
    serialNumber: z.string().trim().min(1).max(160).nullable(),
    assetReference: assetsPlacementAssetReferenceSchema.nullable(),
    presence: assetsLocationPresenceSchema,
    firstSeenAt: z.string().datetime({ offset: true }),
    lastSeenAt: z.string().datetime({ offset: true }),
    lastEventType: z.string().trim().min(1).max(160),
    occurrenceCount: z.number().int().positive(),
});
export const assetsLocationCorrectionProposalSchema = z.strictObject({
    id: z.string().uuid(),
    reviewAssetId: z.string().uuid(),
    assetReference: assetsPlacementAssetReferenceSchema,
    projectReference: projectsProjectReferenceSnapshotSchema,
    proposedPlacedAt: z.string().datetime({ offset: true }),
    status: z.enum(['pending', 'dismissed', 'applied']),
    placementId: z.string().uuid().nullable(),
    createdAt: z.string().datetime({ offset: true }),
});
export const assetsLocationReviewItemSchema = z.strictObject({
    contractVersion: z.literal(assetsLocationReviewCapability),
    tenantId: z.string().uuid(),
    id: z.string().uuid(),
    provider: assetsLocationProviderSchema,
    location: assetsSourceLocationSnapshotSchema,
    reason: assetsLocationReviewReasonSchema,
    status: assetsLocationReviewStatusSchema,
    projectReference: projectsProjectReferenceSnapshotSchema.nullable(),
    firstSeenAt: z.string().datetime({ offset: true }),
    lastSeenAt: z.string().datetime({ offset: true }),
    lastEventType: z.string().trim().min(1).max(160),
    observationCount: z.number().int().positive(),
    revision: z.number().int().positive(),
    resolutionNotes: z.string().trim().min(1).max(2_000).nullable(),
    resolvedByMembershipId: z.string().uuid().nullable(),
    resolvedAt: z.string().datetime({ offset: true }).nullable(),
    assets: z.array(assetsLocationReviewAssetSchema),
    correctionProposals: z.array(assetsLocationCorrectionProposalSchema),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const assetsLocationReviewListQuerySchema = z.strictObject({
    status: assetsLocationReviewStatusSchema.nullable().default('open'),
    limit: z.number().int().min(1).max(250).default(100),
    offset: z.number().int().nonnegative().default(0),
});
const assetsLocationReviewResolutionBaseSchema = z.strictObject({
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    resolutionNotes: z.string().trim().min(10).max(2_000),
});
export const assetsLocationReviewLinkCommandSchema = assetsLocationReviewResolutionBaseSchema.extend({
    kind: z.literal('link-project'),
    projectId: z.string().uuid(),
    assetLinks: z.array(z.strictObject({
        sourceAssetId: z.string().trim().min(1).max(240),
        assetId: z.string().uuid(),
    })).max(250).default([]),
    createCorrectionProposals: z.boolean().default(true),
}).superRefine((value, context) => {
    if (new Set(value.assetLinks.map(({ sourceAssetId }) => sourceAssetId)).size !== value.assetLinks.length) {
        context.addIssue({ code: 'custom', path: ['assetLinks'], message: 'Source Asset links must be unique.' });
    }
    if (new Set(value.assetLinks.map(({ assetId }) => assetId)).size !== value.assetLinks.length) {
        context.addIssue({ code: 'custom', path: ['assetLinks'], message: 'CREST Asset links must be unique.' });
    }
});
export const assetsLocationReviewCloseCommandSchema = assetsLocationReviewResolutionBaseSchema.extend({
    kind: z.enum(['ignore', 'resolve']),
});
export const assetsLocationReviewCommandSchema = z.union([
    assetsLocationReviewLinkCommandSchema,
    assetsLocationReviewCloseCommandSchema,
]);
export const assetsLocationReviewItemResponseSchema = z.strictObject({
    data: assetsLocationReviewItemSchema,
    command: z.strictObject({ commandId: z.string().uuid(), idempotent: z.boolean() }).optional(),
});
export const assetsLocationReviewItemsResponseSchema = z.strictObject({
    data: z.array(assetsLocationReviewItemSchema),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    }),
});
export const assetsLocationObservationResponseSchema = z.strictObject({
    contractVersion: z.literal(assetsLocationProvenanceCapability),
    observationId: z.string().uuid().nullable(),
    review: assetsLocationReviewItemSchema.nullable(),
    skipped: z.boolean(),
    skipReason: z.enum(['base-location']).nullable(),
    reviewCreated: z.boolean(),
    reviewAssetCreated: z.boolean(),
    command: z.strictObject({ commandId: z.string().uuid(), idempotent: z.boolean() }),
});
//# sourceMappingURL=assets-location-provenance.contract.js.map